import React, { useState, useEffect, useMemo } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "src/components/ui/sheet";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import type { PayoutUser } from "../types/commissions.types";
import {
  useGetCommissionReportsQuery,
  useUpdateCommissionReportMutation,
} from "../endpoints/commissions.endpoints";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency } from "../../utils/billing-utils";

function getMonthName(month: number): string {
  return new Date(2000, month - 1, 1).toLocaleString("default", { month: "long" });
}

interface PayoutUserDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: PayoutUser | null;
  isNew: boolean;
  onSave: (user: Partial<PayoutUser> & { id?: string }) => void;
}

const PayoutUserDrawer: React.FC<PayoutUserDrawerProps> = ({
  open,
  onOpenChange,
  user,
  isNew,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [activePercent, setActivePercent] = useState<string>("");
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  /** Editable commission value per report row id (for month-by-month breakdown) */
  const [editingCommissionByReportId, setEditingCommissionByReportId] = useState<Record<string, string>>({});

  const { data: allUserReports = [] } = useGetCommissionReportsQuery(
    { type: "user" },
    { skip: !open || !user?.id || isNew }
  );
  const [updateCommissionReport] = useUpdateCommissionReportMutation();
  const userBreakdownRows = useMemo(() => {
    if (!user?.id) return [];
    const id = user.id;
    const filtered = allUserReports.filter(
      (r) => (r.userId ?? r.user) === id
    );
    return [...filtered].sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));
  }, [allUserReports, user?.id]);

  useEffect(() => {
    if (open) {
      setBreakdownExpanded(false);
      setEditingCommissionByReportId({});
      if (user) {
        setName(user.name);
        setEmail(user.email);
        setActivePercent(String(user.activePercent));
      } else if (isNew) {
        setName("");
        setEmail("");
        setActivePercent("");
      }
    }
  }, [open, user, isNew]);

  const handleSave = () => {
    const activeNum = parseFloat(activePercent);
    onSave({
      ...(user?.id ? { id: user.id } : {}),
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      activePercent: Number.isNaN(activeNum) ? undefined : activeNum,
    });
    onOpenChange(false);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="!w-[60vw] sm:!w-[75vw] !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle>
            {isNew ? "Add Commission User" : "Edit Commission User"}
          </SheetTitle>
        </SheetHeader>
        {/* Name + Email row — same layout as referral drawer */}
        <div className="flex gap-4 py-4 items-end">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="border-gray-300 bg-white"
            />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@company.com"
              className="border-gray-300 bg-white"
            />
          </div>
        </div>
        {/* Rate (%) — above month-by-month breakdown */}
        <div className="flex gap-4 py-4 items-end">
          <div className="flex-1 min-w-0 max-w-xs flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Rate (%)</label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={activePercent}
              onChange={(e) => setActivePercent(e.target.value)}
              placeholder="e.g. 10"
              className="border-gray-300 bg-white"
            />
          </div>
        </div>
        {/* Month-by-month breakdown — same styling as referral drawer */}
        {!isNew && user?.id && (
          <div className="border border-gray-200 rounded-md mb-4">
            <Button
              type="button"
              variant="ghost"
              size="med"
              className="flex items-center gap-2 bg-gray-200 text-black w-full hover:bg-gray-300 rounded-md"
              onClick={() => setBreakdownExpanded((p) => !p)}
            >
              {breakdownExpanded ? (
                <ChevronUp className="h-4 w-4 shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0" />
              )}
              <span>Month-by-month commission breakdown</span>
              {userBreakdownRows.length > 0 && (
                <span className="text-gray-500 font-normal">
                  ({userBreakdownRows.length} month{userBreakdownRows.length !== 1 ? "s" : ""})
                </span>
              )}
            </Button>
            {breakdownExpanded && (
              <div className="overflow-hidden bg-gray-50/50">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-medium text-gray-700">Month</TableHead>
                      <TableHead className="font-medium text-gray-700">Year</TableHead>
                      <TableHead className="font-medium text-gray-700 text-right w-1/4 min-w-[25%]">Commission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userBreakdownRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-gray-500 py-4 text-sm">
                          No commission data for this user yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      userBreakdownRows.map((row) => {
                        const rowKey = row.id ?? `report-${row.year}-${row.month}`;
                        const displayValue = editingCommissionByReportId[rowKey] ?? String(Number((row.commission ?? 0).toFixed(2)));
                        const handleCommissionBlur = () => {
                          if (!row.id) return;
                          const parsed = parseFloat(displayValue);
                          if (!Number.isNaN(parsed) && parsed !== (row.commission ?? 0)) {
                            updateCommissionReport({ id: row.id, commission: parsed }).unwrap().catch(() => {});
                          }
                          setEditingCommissionByReportId((prev) => {
                            const next = { ...prev };
                            delete next[rowKey];
                            return next;
                          });
                        };
                        return (
                          <TableRow key={rowKey} className="bg-white">
                            <TableCell className="text-sm">
                              {getMonthName(row.month)}
                            </TableCell>
                            <TableCell className="text-sm">{row.year}</TableCell>
                            <TableCell className="text-right text-sm font-medium w-1/4 min-w-[25%]">
                              {row.id ? (
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={displayValue}
                                  onChange={(e) =>
                                    setEditingCommissionByReportId((prev) => ({ ...prev, [rowKey]: e.target.value }))
                                  }
                                  onBlur={handleCommissionBlur}
                                  className="h-8 w-full min-w-[6rem] max-w-[10rem] text-right border-gray-300 bg-white ml-auto"
                                />
                              ) : (
                                formatCurrency(Number((row.commission ?? 0).toFixed(2)))
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
        <SheetFooter>
          <Button size="sm" className="px-6" onClick={handleSave}>
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default PayoutUserDrawer;
